import { createFileRoute, Link } from "@tanstack/react-router";
import { fetchTopicsIndex } from "@/lib/home-box.functions";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";

const TITLE = "All Topics — Insurance, Legal & AI Guides | LifeWestWay";
const DESCRIPTION =
  "Browse every LifeWestWay topic hub: car and renters insurance, Liberty Mutual reviews, AI automation and data analytics guides for 2026.";

export const Route = createFileRoute("/topics/")({
  loader: async () => fetchTopicsIndex(),
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  errorComponent: () => (
    <p className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">
      Topics could not be loaded. Please try again.
    </p>
  ),
  notFoundComponent: () => (
    <p className="mx-auto max-w-3xl px-4 py-20 text-muted-foreground">Topics not found.</p>
  ),
  component: TopicsIndexPage,
});

type TopicArticle = { id: string; title: string; slug: string; excerpt: string };
type TopicGroup = { id: string; title: string; slug: string; blurb: string; articles: TopicArticle[] };

function TopicsIndexPage() {
  const { topics } = Route.useLoaderData() as { topics: TopicGroup[] };


  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:px-8">
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Topics" }]} />
      <h1 className="mt-4 font-display text-3xl font-bold text-foreground sm:text-4xl">
        All Topics
      </h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        Every LifeWestWay topic hub and the guides published inside it — insurance, legal help,
        tax filing and AI automation, all researched by our editorial team.
      </p>

      <nav aria-label="Topic hubs" className="mt-8 flex flex-wrap gap-2">
        {topics.map((t) => (
          <a
            key={t.id}
            href={`#${t.slug}`}
            title={t.title}
            className="rounded-full border border-accent/30 px-3 py-1.5 text-xs font-semibold text-accent hover:border-accent"
          >
            {t.title}
          </a>
        ))}
      </nav>

      <div className="mt-12 space-y-12">
        {topics.map((t) => (
          <section key={t.id} id={t.slug}>
            <h2 className="font-display text-2xl font-bold text-accent">
              <Link to="/topics/$slug" params={{ slug: t.slug }} title={t.title}>
                {t.title}
              </Link>
            </h2>
            {t.blurb ? (
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">{t.blurb}</p>
            ) : null}
            {t.articles.length === 0 ? (
              <p className="mt-4 text-sm text-muted-foreground">New guides coming shortly.</p>
            ) : (
              <ul className="mt-4 grid gap-3 sm:grid-cols-2">
                {t.articles.map((a) => (
                  <li
                    key={a.id}
                    className="rounded-xl border border-border bg-card p-4 transition-colors hover:border-accent/70"
                  >
                    <Link
                      to="/topics/$slug/$article"
                      params={{ slug: t.slug, article: a.slug }}
                      title={a.title}
                      className="font-semibold text-foreground hover:text-accent"
                    >
                      {a.title}
                    </Link>
                    {a.excerpt ? (
                      <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">
                        {a.excerpt}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
